/**
 * Filename prefix/suffix helpers for RenameField (date + Basecamp todo id + uniqueness).
 * Order when all on: {todoId}-{date}-{slug}-{uniq}
 *
 * Uniqueness suffix (pilot-feedback): 6-char base36 from Date.now(), or first 8 hex
 * chars of a conversation id. Filename only - never part of display title.
 */

const DATE_PREFIX_RE = /^\d{4}-\d{2}-\d{2}/
/** Conversation-id fragment: first 8 hex chars (no dashes). */
const UNIQ_HEX8_RE = /^[a-f0-9]{8}$/
/**
 * Timestamp base36 slice(-6). Require a digit so common 6-letter words
 * (system, report, status) are not treated as uniqueness tokens.
 */
const UNIQ_BASE36_RE = /^(?=[a-z0-9]*\d)[a-z0-9]{6}$/

/** Today's date as YYYY-MM-DD (local). */
export function todayDatePrefix(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Extract Basecamp todo id from frontmatter when present. */
export function extractTodoIdFromFrontmatter(
  frontmatter: Record<string, unknown> | null | undefined,
): string | null {
  if (!frontmatter || typeof frontmatter !== 'object') return null
  const raw =
    frontmatter.todo_id ??
    frontmatter.basecamp_todo_id ??
    frontmatter.basecampTodoId
  if (raw == null) return null
  const s = String(raw).trim()
  if (/^\d{6,}$/.test(s)) return s
  return null
}

/** True when a trailing filename segment looks like a uniqueness token. */
export function isUniquenessSuffix(segment: string): boolean {
  const s = segment.trim().toLowerCase()
  return UNIQ_HEX8_RE.test(s) || UNIQ_BASE36_RE.test(s)
}

/**
 * Extract trailing uniqueness suffix from a basename (after optional todo/date strip).
 * Returns the token without a leading hyphen, or null.
 */
export function extractUniquenessSuffix(
  name: string,
  todoId: string | null = null,
): string | null {
  const body = stripLeadingPrefixes(name, todoId)
  const parts = body.split('-').filter(Boolean)
  if (parts.length < 2) return null
  const last = parts[parts.length - 1]
  return isUniquenessSuffix(last) ? last : null
}

/** Strip leading todo + date prefixes only (keeps uniqueness suffix). */
function stripLeadingPrefixes(name: string, todoId: string | null): string {
  let rest = name.trim().replace(/\.md$/i, '')
  if (todoId) {
    const todoRe = new RegExp(`^${todoId}-`)
    rest = rest.replace(todoRe, '')
  }
  rest = rest.replace(/^\d{4}-\d{2}-\d{2}-/, '')
  return rest
}

/** Strip known todo + date prefixes and trailing uniqueness suffix. */
export function stripKnownPrefixes(name: string, todoId: string | null): string {
  let rest = stripLeadingPrefixes(name, todoId)
  const parts = rest.split('-').filter(Boolean)
  if (parts.length >= 2 && isUniquenessSuffix(parts[parts.length - 1])) {
    parts.pop()
    rest = parts.join('-')
  }
  return rest
}

/** Detect whether basename already starts with the given todo id. */
export function hasTodoPrefix(name: string, todoId: string | null): boolean {
  if (!todoId) return false
  const base = name.trim().replace(/\.md$/i, '')
  return base === todoId || base.startsWith(`${todoId}-`)
}

/** Detect leading YYYY-MM-DD after optional todo prefix. */
export function hasDatePrefix(name: string, todoId: string | null): boolean {
  let rest = name.trim().replace(/\.md$/i, '')
  if (todoId && (rest === todoId || rest.startsWith(`${todoId}-`))) {
    rest = rest === todoId ? '' : rest.slice(todoId.length + 1)
  }
  return DATE_PREFIX_RE.test(rest)
}

/** Detect trailing uniqueness suffix after optional todo/date prefixes. */
export function hasUniquenessSuffix(
  name: string,
  todoId: string | null = null,
): boolean {
  return extractUniquenessSuffix(name, todoId) != null
}

/** Build filename body (no .md) from slug + prefix/suffix toggles. */
export function buildPrefixedName(opts: {
  slug: string
  dateOn: boolean
  todoOn: boolean
  todoId: string | null
  date?: string
  uniqOn?: boolean
  uniqSuffix?: string | null
}): string {
  const parts: string[] = []
  if (opts.todoOn && opts.todoId) parts.push(opts.todoId)
  if (opts.dateOn) parts.push(opts.date ?? todayDatePrefix())
  const slug = opts.slug.replace(/^\.+/, '').replace(/\.md$/i, '').trim()
  if (slug) parts.push(slug)
  if (opts.uniqOn && opts.uniqSuffix) parts.push(opts.uniqSuffix)
  return parts.join('-')
}

export type PrefixChunk = 'todo' | 'date' | 'uniq'
export type NameSegment = { key: PrefixChunk | 'slug'; text: string }

/** Split filename into todo / date / slug / uniq segments for hover highlight. */
export function segmentFilename(value: string, todoId: string | null): NameSegment[] {
  const segments: NameSegment[] = []
  let rest = value.replace(/\.md$/i, '')

  if (todoId && (rest === todoId || rest.startsWith(`${todoId}-`))) {
    const withHyphen = rest.startsWith(`${todoId}-`)
    segments.push({ key: 'todo', text: withHyphen ? `${todoId}-` : todoId })
    rest = withHyphen ? rest.slice(todoId.length + 1) : ''
  }

  const dateMatch = rest.match(/^(\d{4}-\d{2}-\d{2})(-)?/)
  if (dateMatch) {
    const full = dateMatch[0]
    segments.push({ key: 'date', text: full })
    rest = rest.slice(full.length)
  }

  const uniq = extractUniquenessSuffix(rest, null)
  let slugText = rest
  if (uniq) {
    const suffix = `-${uniq}`
    if (slugText.endsWith(suffix)) {
      slugText = slugText.slice(0, -suffix.length)
    } else if (slugText === uniq) {
      slugText = ''
    }
  }

  if (slugText.length > 0 || (segments.length === 0 && !uniq)) {
    segments.push({ key: 'slug', text: slugText })
  }

  if (uniq) {
    const needsHyphen = segments.length > 0 && slugText.length > 0
    segments.push({ key: 'uniq', text: needsHyphen || segments.length > 0 ? `-${uniq}` : uniq })
  }

  return segments
}

/** Sanitize LLM slug output to filename-safe kebab case. */
export function sanitizeSuggestedSlug(raw: string, maxLen = 60): string {
  let s = raw
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/^#+\s*/, '')
    .replace(/\.md$/i, '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')

  // Drop accidental date/todo-looking leading segments from model output
  s = s.replace(/^\d{4}-\d{2}-\d{2}-/, '')
  s = s.replace(/^\d{6,}-/, '')

  // Drop accidental uniqueness-looking trailing segment
  const parts = s.split('-').filter(Boolean)
  if (parts.length >= 2 && isUniquenessSuffix(parts[parts.length - 1])) {
    parts.pop()
    s = parts.join('-')
  }

  if (s.length > maxLen) {
    s = s.slice(0, maxLen).replace(/-+$/g, '')
  }
  return s || 'untitled'
}

/** True when a slug looks like leaked prompt prose rather than a real name. */
export function looksLikePromptLeakSlug(slug: string): boolean {
  const s = slug.toLowerCase()
  return (
    /\b(this-slug|slug-captures|kebab-case|no-date|filename-slug|url-friendly|max-\d+)\b/.test(
      s,
    ) || /^(slug|title|filename)(-|$)/.test(s)
  )
}

/** Title Case from a slug or free text (for Apply-from-title inverse / display). */
export function titleCaseFromSlug(raw: string): string {
  const words = raw
    .replace(/\.md$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** Slugify a human title for the filename body (no date/todo/uniq affixes). */
export function slugifyTitle(title: string, maxLen = 60): string {
  return sanitizeSuggestedSlug(title, maxLen)
}
